import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { TicketWeg } from "../../../views/suppliers/LiefervertragObjekt.js";
import type { ObjektZustand } from "../ObjektArbeitsdialog.js";
import type { InhaltProps } from "../ObjektPanelGastgeber.js";

/**
 * Laden eines Objekts im Panel (F13): `null` beim ersten Laden, danach der Stand oder der Fehler. `neu` lädt im Stehen —
 * der alte Stand bleibt, bis der neue da ist (F9); `runde` zählt jedes geglückte Laden (Fälligkeiten lesen damit frisch).
 */
export function useObjektLaden<T>(laden: () => Promise<T>): { stand: T | Error | null; neu: () => void; runde: number } {
  const [stand, setStand] = useState<T | Error | null>(null);
  const [runde, setRunde] = useState(0);
  const lauf = useRef(0);
  const neu = useCallback(() => {
    const n = ++lauf.current;
    laden().then((x) => { if (n === lauf.current) { setStand(x); setRunde((r) => r + 1); } },
      (e: unknown) => { if (n === lauf.current) setStand(e instanceof Error ? e : new Error(String(e))); });
  }, [laden]);
  useEffect(() => { neu(); }, [neu]);
  return { stand, neu, runde };
}

/** Der Zustand des Panels aus dem Ladestand (F13): eine 404 heißt „gibt es nicht (mehr)“, alles andere Ladefehler. */
export function ladeZustand(stand: unknown, t: { missing: string; loadOneFailed: string }): ObjektZustand {
  if (stand === null) return { art: "laedt" };
  if (!(stand instanceof Error)) return { art: "bereit" };
  return stand instanceof ApiError && stand.status === 404 ? { art: "fehlt", text: t.missing } : { art: "fehler", text: t.loadOneFailed };
}

/** Der Ticketweg im Gastgeber: nur mit eigener Kennung im vollen Zuschnitt (F12) — sonst kein „Ticket anlegen“. */
export function ticketWegAus(p: Pick<InhaltProps, "client" | "rechte">): { ticket?: TicketWeg } {
  const ich = p.rechte.ich;
  return ich === null || !p.rechte.zuschnittVoll ? {}
    : { ticket: { client: p.client, ich: { id: ich.id, name: ich.name }, arbeitsunternehmenId: null } };
}
